import { Injectable } from '@angular/core';
import { DbTransaction } from '../models/db/db-transaction';
import { StoreName } from '../entities/StoreName';
import { RepositoryService } from '../baseServices/repository.service';
import { AzureBlobStorageService } from '../baseServices/azure-blob-storage.service';
import { GlobalConstants } from '../constant/constant';
import { OdometerReadingMedia, OdometerPhotoType, UploadStatus } from '../entities/OdometerReadingMedia';
import { LogService } from '../baseServices/logging/log.service';
import { BlobType } from '../enums/blob-storage/blob-type.enum';
import { dataUrlToBlob } from '../utils/converters/to-blob.util';
import { RecordNotFoundException } from '../utils/exceptions';

@Injectable({
  providedIn: 'root',
})
export class OdometerReadingMediaService {
  constructor(
    private repository: RepositoryService,
    private azureBlobStorageService: AzureBlobStorageService,
    private logService: LogService
  ) {}

  // #region Utility

  private async startTransactionIfNeeded(existingTransaction?: DbTransaction): Promise<DbTransaction> {
    return existingTransaction || (await this.repository.startTransaction([StoreName.OdometerReadingMedia], 'readwrite'));
  }

  // #endregion
  // #region Low-level Get One

  async getOdometerReadingMediaByRecordId(recordId: number, existingTransaction?: DbTransaction): Promise<OdometerReadingMedia> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getByLocalId<OdometerReadingMedia>(StoreName.OdometerReadingMedia, recordId, OdometerReadingMedia, transaction);
    if (result === null) {
      throw new RecordNotFoundException(recordId);
    }

    const record = result as OdometerReadingMedia;

    return record;
  }

  // #endregion
  // #region Low-level Get Many

  async getAllOdometerReadingMedia(existingTransaction?: DbTransaction): Promise<OdometerReadingMedia[]> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    const result = await this.repository.getAll<OdometerReadingMedia>(
      StoreName.OdometerReadingMedia,
      undefined,
      undefined,
      transaction
    );

    return result as OdometerReadingMedia[];
  }

  // #endregion
  // #region Low-level Create

  async createOdometerReadingMedia(recordToCreate: OdometerReadingMedia, existingTransaction?: DbTransaction): Promise<number> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const recordId = await this.repository.create(recordToCreate, StoreName.OdometerReadingMedia, transaction);
    return recordId;
  }

  // #endregion
  // #region Low-level Update

  async updateOdometerReadingMedia(recordToUpdate: OdometerReadingMedia, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    await this.repository.update(recordToUpdate, StoreName.OdometerReadingMedia, transaction);
  }

  // #endregion
  // #region Low-level Delete

  async deleteOdometerReadingMedia(recordId: number, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    await this.repository.delete(recordId, StoreName.OdometerReadingMedia, transaction);
  }

  // #endregion
  // #region Media Upload Management

  /**
   * Sets the uploaded flag to true for an odometer reading media record
   * @param recordId - The record ID of the media to update
   * @param existingTransaction - Optional database transaction
   */
  async setMediaAsUploadedWithFileName(recordId: number, fileName: string, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // Get existing record
    const existingRecord = await this.getOdometerReadingMediaByRecordId(recordId, transaction);

    if(existingRecord.UploadStatus === UploadStatus.Uploaded) return;

    // Update the uploaded flag
    existingRecord.UploadStatus = UploadStatus.Uploaded;
    existingRecord.BlobFileName = fileName;

    // Update the record
    await this.updateOdometerReadingMedia(existingRecord, transaction);

    this.logService.event('OdometerMedia.UploadedFlagUpdated', {
      recordId: recordId,
      uploadStatus: UploadStatus.Uploaded,
      fileName: fileName
    });
  }

  /**
   * Sets the upload status to failed for an odometer reading media record
   * @param recordId - The record ID of the media to update
   * @param existingTransaction - Optional database transaction
   */
  async setMediaAsUploadFailed(recordId: number, existingTransaction?: DbTransaction): Promise<void> {
    const transaction = await this.startTransactionIfNeeded(existingTransaction);

    // Get existing record
    const existingRecord = await this.getOdometerReadingMediaByRecordId(recordId, transaction);

    // Update the upload status to failed
    existingRecord.UploadStatus = UploadStatus.Failed;
    existingRecord.BlobFileName = null;

    // Update the record
    await this.updateOdometerReadingMedia(existingRecord, transaction);

    this.logService.event('OdometerMedia.UploadFailedFlagUpdated', {
      recordId: recordId,
      uploadStatus: UploadStatus.Failed
    });
  }

  // #endregion
  // #region Upload Media to Azure Blob

  /**
   * Uploads odometer reading media to blob storage if needed, sets local filename and uploaded flag
   * @param odometerReadingMediaRecordId - The record ID of the media to upload
   * @param existingTransaction - Optional database transaction
   * @returns The blob file name if successful, null otherwise
   */
  async uploadOdometerReadingMediaIfNeeded(
    odometerReadingMediaRecordId: number,
    existingTransaction?: DbTransaction
  ): Promise<string | null> {
    if (!odometerReadingMediaRecordId) {
      return null;
    }

    const transaction = await this.startTransactionIfNeeded(existingTransaction);
    const existingMediaRecord = await this.getOdometerReadingMediaByRecordId(odometerReadingMediaRecordId, transaction);
    
    if(existingMediaRecord.UploadStatus === UploadStatus.Uploaded) return existingMediaRecord.BlobFileName;

    try {

      const { uriToken, blobFileName } = await this.azureBlobStorageService.upload(
        dataUrlToBlob(existingMediaRecord.Value) as File,
        BlobType.ODOMETER_READING,
        existingMediaRecord.FileFormat
      );

      await this.setMediaAsUploadedWithFileName(odometerReadingMediaRecordId, blobFileName);

      this.logService.event('OdometerMedia.PhotoUploaded', {
        odometerReadingMediaRecordId: existingMediaRecord.RecordId,
        photoType: existingMediaRecord.PhotoType,
        fileName: blobFileName
      });

      return blobFileName;

    } catch (error) {
      this.logService.error('OdometerMedia.PhotoUploadFailed',
        error, {
        odometerReadingMediaRecordId: existingMediaRecord.RecordId,
        photoType: existingMediaRecord.PhotoType,
      });
      
      // Set the upload status as failed
      await this.setMediaAsUploadFailed(odometerReadingMediaRecordId);
      
      return null;
    }
  }

  // #endregion
}
